import { Object } from 'core-js';
import { completedMessage } from 'src/modules/utils';

// Response logic (branching) helpers. Types of response triggers.
function getAndTriggers(responseTriggers, userResponses) {
  if (typeof responseTriggers === "undefined" || !responseTriggers || responseTriggers.length === 0) {
    return null;
  }

  let foundTrigger = null;
  const delimiter = ",";
  const triggers = responseTriggers.filter(x => x.triggerResponseValue && x.triggerResponseValue.indexOf(delimiter) > -1);
  if (triggers) {
    foundTrigger = triggers.find(trigger => {
      let values = trigger.triggerResponseValue.split(delimiter);
      let matches = 0;
      let responseIdsToTrigger = userResponses.filter(x => x.hspCategoryQuestionId === trigger.triggerCategoryQuestionId).map(x => x.hspResponseId);
      responseIdsToTrigger.forEach(x => {
        if (values.includes(x.toString())) {
          matches++;
        }
      });

      if (matches === values.length) {
        return true;
      }

      return false;          
    });
  }

  return typeof foundTrigger !== "undefined" && foundTrigger ? foundTrigger : null;
}

function getOrTriggers(responseTriggers, userResponses) {
  if (typeof responseTriggers === "undefined" || !responseTriggers || responseTriggers.length === 0) {
    return null;
  }

  let foundTrigger = null;     
  const delimiter = "|";
  const triggers = responseTriggers.filter(x => x.triggerResponseValue && x.triggerResponseValue.indexOf(delimiter) > -1);
  if (triggers) {
    foundTrigger = triggers.find(trigger => {
      let responseIdsToTrigger = userResponses.filter(x => x.hspCategoryQuestionId === trigger.triggerCategoryQuestionId).map(x => x.hspResponseId);
      return responseIdsToTrigger.find(x => trigger.triggerResponseValue.indexOf(x.toString()) >= 0);
    });
  }

  return typeof foundTrigger !== "undefined" && foundTrigger ? foundTrigger : null;
}

function getSingleResponseTrigger(responseTriggers, userResponses) {
  if (typeof responseTriggers === "undefined" || !responseTriggers || responseTriggers.length === 0) {
    return null;
  }

  let foundTrigger = null;     
  let triggersByResponseId = responseTriggers.filter(x => x.triggerResponseId !== null);
  if (triggersByResponseId && triggersByResponseId.length > 0) {
    let respondedToTrigger = triggersByResponseId.find(trigger => 
      userResponses.find(x => 
        x.hspCategoryQuestionId === trigger.triggerCategoryQuestionId &&
        x.hspResponseId === trigger.triggerResponseId));
    if (respondedToTrigger) {
      foundTrigger = respondedToTrigger;
    }
  }

  return typeof foundTrigger !== "undefined" && foundTrigger ? foundTrigger : null;
}

function getNullTrigger(responseTriggers) {
  if (typeof responseTriggers === "undefined" || !responseTriggers || responseTriggers.length === 0) {
    return null;
  }

  let foundTrigger = null;     
  let nullTriggers = responseTriggers.filter(x => 
      x.triggerResponseId === null && 
      (x.triggerResponseValue === null || x.triggerResponseValue.trim().length === 0) && 
      x.targetSectionId > 0);
  if (nullTriggers && nullTriggers.length > 0)
  {
    foundTrigger = nullTriggers[0];
  }

  return typeof foundTrigger !== "undefined" && foundTrigger ? foundTrigger : null;
}

function _isNullOrUndefined(obj) {
  return typeof obj === "undefined" || obj === null;
}

function _isNullOrEmpty(obj) {
  return _isNullOrUndefined(obj) || obj.length === 0;
}

function _surveyHasResponseTriggers(sections) {
  let trigger = sections.find(x => !_isNullOrUndefined(x.responseTriggers) && x.responseTriggers.length > 0);
  return trigger ? true : false;
}
// End Response logic (branching) helpers. Types of response triggers.

function getRequiredQuestions(section, types) {
  if (_isNullOrEmpty(section)) {
    return null;
  }

  const questions = section.categories.flatMap(cat => 
    cat.categoryQuestions.filter(x => 
      x.isRequired === true && 
      (
        x.hspQuestionTypeId < types.RECAPTCHA ||
        (x.hspQuestionTypeId === types.RECAPTCHA && x.hspResponseSetId !== null) // Custom reCAPTCHA checkbox
      )
    )
  );

  return questions;
}

// NOTE: Needed to add the 'state' object as one of the params to make this export as a 'function' correctly.
//       Without the 'state' object, this does not show up as a function in the component it's imported, i.e., 'isNullOrUndefined' is not a function.
export const isNullOrUndefined = state => obj => {
  return _isNullOrUndefined(obj);
}

export const surveyHasResponseTriggers = state => {
  return _surveyHasResponseTriggers(state.activeSurvey.sections);
}

export const windowWidth = state => {
  return state.windowWidth;
}

export const constants = state => {
  return state.constants;
}

export const questionTypes = state => {
  return state.constants.questionTypes;
}

export const questionTypeOptions = state => {
  return state.constants.questionTypeOptions;
}

export const displayTypes = state => {
  return state.constants.displayTypes;
}

export const displayTypeOptions = state => {
  return state.constants.displayTypeOptions;
}

export const dataCompletenessCheckTypes = state => {
  return state.constants.completenessValidationTypes;
}

export const surveys = state => {
  return state.surveys;
}

export const orgSurveys = state => {
  return state.orgSurveys;
}

export const themeColor = state => id => {
  let survey = state.orgSurveys.find(survey => survey.surveyUrlId === id);

  if(!survey) {
    return null;
  }

  let theme = survey.displayOptions.find(disOpt => disOpt.optionName === 'primaryColorScheme');

  if(theme) {
    return theme.optionValue;
  }

  return null;
}

export const colorScheme = state => {
  return state.activeColorScheme;
}

export const debugQuestions = state => {
  return state.debug.questions;
}

export const getSurveyById = state => id => {
  return state.surveys.find(survey => survey.hspSurveyId.toLowerCase() === id.toLowerCase())
}

export const getOrgSurveyByUrlId = state => id => {
  return state.orgSurveys.find(survey => survey.surveyUrlId === id);
}

export const getOrgSurveyById = state => id => {
  return state.orgSurveys.find(survey => survey.orgSurveyId === id);
}

export const getNextSectionId = state => {
  let sectionIndex = state
    .activeSurveyNavSections
    .findIndex(section => section.id === state.activeSectionId);
  let activeSection = state.activeSurveyNavSections[sectionIndex];

  // Active section is last section. No additional sections available.
  if (sectionIndex === state.activeSurveyNavSections.length - 1) {
    return null;
  }

  sectionIndex++;
  //console.log(`+A ${sectionIndex}`)

  // No target section specified or last section.
  if (activeSection.targetSectionId === null || sectionIndex === state.activeSurveyNavSections.length - 1) {
    //console.log(`+B ${sectionIndex}`);
    return state.activeSurveyNavSections[sectionIndex].id;
  }

  // Find the target section ID
  while (state.activeSurveyNavSections[sectionIndex].id !== activeSection.targetSectionId && (sectionIndex + 1) < state.activeSurveyNavSections.length) {
    sectionIndex++;
    //console.log(`+C ${sectionIndex}`)
  }

  return state.activeSurveyNavSections[sectionIndex].id;
}

export const getNavSectionById = state => id => {
  return state.activeSurveyNavSections.find(section => section.id === id);
}

export const getNavSectionIndexById = state => id => {
  return state.activeSurveyNavSections.findIndex(section => section.id === id);
}

export const getRequiredSectionById = state => id => {
  const types = state.constants.questionTypes;
  const sections = state.activeSurvey.sections;
  const section = sections.find(x => x.hspSectionId === id);
  const questions = getRequiredQuestions(section, types);

  return _isNullOrEmpty(questions) ? null : section;
}

export const getSectionByQuestionId = state => id => {
  if (!id) {
    return null;
  }

  let section = state.activeSurvey.sections.find(s => {
    return s.categories.find(c => {
      return c.categoryQuestions.find(x => x.hspCategoryQuestionId === id);
    });
  });

  return section;
}

export const activeSurvey = state => {
  return state.activeSurvey;
}

export const activeSurveyDisplayOptions = state => {
  return state.activeSurvey.displayOptions;
}

export const getDisplayOptionsByType = state => (displayTypeId) => {
  return state.activeSurvey.displayOptionsMap[displayTypeId];
}

export const activeSurveySections = state => {
  return state.activeSurvey.sections;
}

export const activeSurveyNavSections = state => {
  return state.activeSurveyNavSections;
}

export const activeSectionId = state => {
  return state.activeSectionId;
}

export const isFirstSection = state => {
  return state.activeSectionId === state.activeSurvey.sections[0].hspSectionId;
}

export const isLastSection = state => {
  let isLast = false;
  if (state.activeSectionId === state.activeSurvey.sections[state.activeSurvey.sections.length - 1].hspSectionId) {
    isLast = true;
  }
  else {
    // Check if the target section has a custom 'Thank You' message.
    const currentNavSection = state.activeSurveyNavSections.find(x => x.id === state.activeSectionId);
    const nextSection = state.activeSurvey.sections.find(x => x.hspSectionId === currentNavSection.targetSectionId);
    if (nextSection) {
      const completedMsg = completedMessage(nextSection);
      if (!_isNullOrEmpty(completedMsg)) {
        // If the target section has the 'Finish' keyword in the second field of the section name delimited by the pipe '|', 
        // then the referring page should display the 'Next' button as 'Finish'.
        const info = nextSection.sectionName.toLowerCase().split("|");
        if (info.length > 1 && info[1].indexOf("finish") >= 0) {
          isLast = true;
        }
      }
    }
  }

  return isLast;
}

export const isLastQuestionsSection = state => (hasResponseTriggers = _surveyHasResponseTriggers(state.activeSurvey.sections)) => {
  if (!state.activeSectionId) {
    return false;
  }

  const userResponses = state.surveyUser.responses;
  let hasMoreQuestions = false;
  let activeIndex = state.activeSurvey.sections.findIndex(x => x.hspSectionId === state.activeSectionId);
  let nextIndex = activeIndex;
  if (!hasResponseTriggers) {
    nextIndex++;
  }
  else {
    let activeSection = state.activeSurvey.sections[activeIndex];
    if (activeSection.responseTriggers.length > 0) {
      let andResponse = getAndTriggers(activeSection.responseTriggers, userResponses);
      let orResponse = getOrTriggers(activeSection.responseTriggers, userResponses);
      if (andResponse && orResponse) {
        let targetSectionId = andResponse.triggerOrdinal < orResponse.triggerOrdinal ? andResponse.targetSectionId : orResponse.targetSectionId;
        nextIndex = state.activeSurvey.sections.findIndex(x => x.hspSectionId === targetSectionId);
      }
      else if (andResponse) {
        nextIndex = state.activeSurvey.sections.findIndex(x => x.hspSectionId === andResponse.targetSectionId);
      }
      else if (orResponse) {
        nextIndex = state.activeSurvey.sections.findIndex(x => x.hspSectionId === orResponse.targetSectionId);
      }
      else {
        // Single response triggers, e.g., radio buttons.
        let singleResponseTrigger = getSingleResponseTrigger(activeSection.responseTriggers, userResponses);
        if (singleResponseTrigger) {
          nextIndex = state.activeSurvey.sections.findIndex(x => x.hspSectionId === singleResponseTrigger.targetSectionId);
        }
        else {
          // null triggers
          let nullTrigger = getNullTrigger(activeSection.responseTriggers);
          if (nullTrigger) {
            nextIndex = state.activeSurvey.sections.findIndex(x => x.hspSectionId === nullTrigger.targetSectionId);
          }
          else {
            nextIndex++;
          }
        }
      }
    }
    else {
      nextIndex++;
    }
  }

  const types = state.constants.questionTypes;
  for (var i = nextIndex; i < state.activeSurvey.sections.length; i++) {
    let section = state.activeSurvey.sections[i];
    if (typeof section.categories === "undefined" || !section.categories || section.categories.length === 0) {
      continue;
    }

    let questions = getRequiredQuestions(section, types);
    if (!_isNullOrEmpty(questions)) {
      hasMoreQuestions = true;
      break;
    }
  }

  return !hasMoreQuestions;
}

export const getSectionResponseTriggers = state => id => {
  let section = state.activeSurvey.sections
    .find(section => section.hspSectionId === id);

  return section.responseTriggers
    .filter(trigger => trigger.triggerCategoryQuestionId === null);
}

export const activeSurveyRequiredQuestions = state => {
  const userResponses = state.surveyUser.responses;
  const sections = state.activeSurvey.sections;
  const types = state.constants.questionTypes;
  if (userResponses.length === 0) {
    let allQs = sections.flatMap(x => 
      getRequiredQuestions(x, types)
    );

    return allQs;
  }

  let requiredQs = [];

  let i = 0;
  // If state.activeSectionId is null, that means we're about to navigate out of the survey. However, we still want to return all the required questions,
  // so use state.activeSurvey.sections.length to search through all the sections.
  let activeSectionIndex = state.activeSectionId ? state.activeSurvey.sections.findIndex(x => x.hspSectionId === state.activeSectionId) : state.activeSurvey.sections.length;
  while (i < activeSectionIndex) {
    const section = sections[i];
    
    // If there are branching logic, depending on the user's responses, figure out how many required questions from the responses and branching.
    // Get response triggers, if any.
    if (section.responseTriggers.length > 0) {
      let targetSectionId = -1;
      
      let andResponse = getAndTriggers(section.responseTriggers, userResponses);
      let orResponse = getOrTriggers(section.responseTriggers, userResponses);
      if (andResponse && orResponse) {
        targetSectionId = andResponse.triggerOrdinal < orResponse.triggerOrdinal ? andResponse.targetSectionId : orResponse.targetSectionId;
      }
      else if (andResponse) {
        targetSectionId = andResponse.targetSectionId;
      }
      else if (orResponse) {
        targetSectionId = orResponse.targetSectionId;
      }
      else {
        // Single response triggers, e.g., radio buttons.
        let singleResponseTrigger = getSingleResponseTrigger(section.responseTriggers, userResponses);
        if (singleResponseTrigger) {
          targetSectionId = singleResponseTrigger.targetSectionId;
        }
        else {
          // null triggers
          let nullTrigger = getNullTrigger(section.responseTriggers);
          if (nullTrigger) {
            targetSectionId = nullTrigger.targetSectionId;
          }
        }
      }

      const sectionIndex = sections.findIndex(x => x.hspSectionId === targetSectionId);
      if (sectionIndex > -1) {
        i = sectionIndex;
      }
      else {
        i++;
      }
    }
    // No triggers, go to the next section.
    else {
      i++;
    }

    const questions = getRequiredQuestions(section, types);
    if (!_isNullOrEmpty(questions)) {
      requiredQs = requiredQs.concat(questions);
    }
  }

  return requiredQs;
}

export const surveyUser = state => {
  return state.surveyUser;
}

export const surveyInitialized = state => {
  return state.activeSurveyInitialized;
}

export const getAllQuestionResponses = state => {
  return state.surveyUser.responses;
}

export const getQuestionResponses = state => id => {
  let responses = state
    .surveyUser
    .responses
    .filter(response => response.hspCategoryQuestionId == id);

  return responses;
}

export const getQuestionResponseId = state => id => {
  let response = state
    .surveyUser
    .responses
    .find(response => response.hspCategoryQuestionId == id);

  return response ? response.hspResponseId : null;
}

export const getQuestionResponseIds = state => id => {
  let response = state
    .surveyUser
    .responses
    .filter(response => response.hspCategoryQuestionId == id)
    .map(response => response.hspResponseId);

  return response;
}

export const firstQuestionNavSectionIndex = state => {
  let index = state.activeSurveyNavSections.findIndex(s => !_isNullOrEmpty(s.questionIds));

  return index;
}

export const firstQuestionSectionIndex = state => {
  let index = state.activeSurvey.sections.findIndex(s => {
    return s.categories.find(c => !_isNullOrEmpty(c.categoryQuestions));
  });

  return index;
}

export const getActiveSurveyReports = state => {
  return state.activeSurveyReports;
}

export const authCheckTime = state => {
  return state.auth.authCheckTime;
}

export const adminUser = state => {
  return state.auth.user;
}

export const adminUserRoles = state => {
  return state.auth.user.roles;
}

export const adminUserName = state => {
  return state.auth.user.username;
}

export const hasRole = state => role => {
  if(state.auth.adminOverride === true) {
    return true;
  }

  let result = state.auth.user.roles.find(userRole => {
    return userRole.name === 'administrator' || role.indexOf(userRole.name) > -1;
  });

  return result !== undefined ? true : false;
}

export const instanceInfo = state => {
  return state.auth.instanceInfo;
}

export const redirectRoute = state => {
  return state.redirectRoute;
}

const _roleNames = (state) => {
  return Object.keys(state.constants.roleLevels);
}

const _defaultPermissionLevel = (state) => {
  return Math.max(...Object.values(state.constants.roleLevels));
};

const _permissionLevels = (state) => {
  return state.constants.roleLevels;
}

export const permissionLevels = state => {
  return _permissionLevels(state);
}

const _adminUserPermissionLevel = (state) => {
  let level = _defaultPermissionLevel(state);
  const names = _roleNames(state);
  state.auth.user.roles.forEach(x => {
    let indexOf = names.indexOf(x.name);
    if (indexOf > -1) {
      const foundLevel = state.constants.roleLevels[x.name];
      if (foundLevel < level) {
        level = foundLevel;
      }
    }
  });

  return level;
}

export const adminUserPermissionLevel = state => {
  return _adminUserPermissionLevel(state);
}

export const adminUserIsUser = state => {
  return _adminUserPermissionLevel(state) >= _permissionLevels(state).user;
}

export const adminUserIsInternalAdmin = state => {
  return _adminUserPermissionLevel(state) <= _permissionLevels(state).analyst;
}

export const userAssignments = state => {
  return !_isNullOrUndefined(state.auth.user.assignments) ? state.auth.user.assignments : null;
}